/**
 * What actually travels when the slot settings page saves.
 *
 * Only CHANGED settings are sent, diffed in wire form against what the server sent, so an untouched
 * form saves nothing and the undo history stays meaningful. That makes the BASELINE load-bearing: it
 * is the definition of "unchanged", and anything that moves it silently un-sends an edit.
 *
 * That is not hypothetical. Setting #7 (deviceID) could not be saved at all - pick device8, press
 * Save, and it came back device1 - because a "normalize deviceID to UUID" effect re-ran on every
 * change to `settings.deviceID` and moved BOTH settings and baseline to the selected value. The
 * user's edit and the baseline ended up identical, the diff found nothing, and the field was never
 * put on the wire. The normalization was itself a no-op: /uiapi/devices returns `id` = the deviceN
 * string, so there was no UUID to convert to. Its only observable effect was the bug.
 *
 * Hence this being a pure function with tests rather than a loop inside a click handler.
 */

/** A form value in the shape the server compares and stores. */
export function toWire(v) {
  if (v === true) return 'true';
  if (v === false) return 'false';
  if (v === null || v === undefined) return null;
  return String(v);
}

/**
 * Diff the form against the baseline.
 *
 * @param settings   current form values, keyed by field name
 * @param baseline   what the server sent, same keys; a field absent here was never sent by the
 *                   server and is NEVER invented back at it
 * @param metadata   FIELD_METADATA - field name -> { number, ... }
 * @param identityFields names needing an explicit confirmation before they travel
 * @returns { changed: { [settingNumber]: wireValue }, changedIdentity: string[] }
 */
export function diffSettings(settings, baseline, metadata, identityFields = []) {
  const changed = {};
  const changedIdentity = [];
  if (!settings || !baseline || !metadata) {
    return { changed, changedIdentity };
  }

  Object.entries(metadata).forEach(([field, meta]) => {
    if (!meta || meta.number === undefined || meta.number === null) return;
    // The server never sent this id; do not invent it.
    if (!Object.prototype.hasOwnProperty.call(baseline, field)) return;

    const cur = toWire(settings[field]);
    const base = toWire(baseline[field]);
    if (cur !== base) {
      changed[meta.number] = cur;
      if (identityFields.includes(field)) changedIdentity.push(field);
    }
  });

  return { changed, changedIdentity };
}
